import type { QueryHookOptions } from "@apollo/client";
import { useQuery, gql } from "@apollo/client";

import type { BrandFragment } from "@shared/data/fragments/BrandFragment/BrandFragment.fragment";
import type { PageInfoFragment } from "@shared/data/fragments/PageInfoFragment/PageInfoFragment.fragment";
import { PAGE_INFO_FRAGMENT } from "@shared/data/fragments/PageInfoFragment/PageInfoFragment.fragment";
import type { BrandFilterListField, TopBrand } from "@shared/types/graphqlGenerated";

const Brands_Shared_Query = gql`
  query Brands_Shared_Query(
    $filters: BrandFilterObject
    $first: Int
    $last: Int
    $after: String
    $before: String
  ) {
    viewer {
      brands(filters: $filters, first: $first, last: $last, after: $after, before: $before) {
        nodes {
          id
          logo
          name
          prescriptionOnly
          topBrand {
            id
            position
          }
        }
        pageInfo {
          ...PageInfoFragment
        }
        totalCount
      }
    }
  }
  ${PAGE_INFO_FRAGMENT}
`;

interface BrandSplinter extends Pick<BrandFragment, "id" | "logo" | "name" | "prescriptionOnly"> {
  topBrand: Pick<TopBrand, "id" | "position">;
}

interface BrandsData {
  viewer: {
    brands: {
      nodes: BrandSplinter[];
      pageInfo: PageInfoFragment;
      totalCount: number;
    };
  };
}

interface Variables {
  filters?: {
    list?: BrandFilterListField;
    letter?: string;
    active?: boolean;
  };
  first?: number;
  last?: number;
  after?: string;
  before?: string;
}

const defaultVariables: Variables = {
  first: 24,
};

const useBrandsQuery = (options?: QueryHookOptions<BrandsData, Variables>) => {
  const { variables } = { variables: {}, ...options };

  return useQuery<BrandsData, Variables>(Brands_Shared_Query, {
    ...options,
    variables: { ...defaultVariables, ...variables },
  });
};

export type { BrandSplinter, Variables, BrandsData };

/* eslint-disable @fullscript/gql-no-manual-hook-declaration */
export { useBrandsQuery, Brands_Shared_Query };
